import { HttpClient } from './HttpClient.js';
import { fromClinicDataList } from './dto/ClinicData.js';
import { fromUserData, fromUserDataList, toUserUpdateRequest } from './dto/UserData.js';
import { fromUserRoleMap } from './dto/UserRoleData.js';

/**
 * IdentityService - Handles user and clinic identity operations
 * Uses HttpClient directly since these endpoints are on the identity server
 */
export class IdentityService {
  constructor() {
    this.httpClient = new HttpClient();
    
    // Environment configuration
    this.authBaseUrl = process.env.REACT_APP_IDENTITY_SERVER_URL;
    
    console.log('IdentityService initialized');
  }

  /**
   * Fetch clinics for the signed-in user
   * @returns {Promise<import('./dto/ClinicData.js').ClinicData[]>}
   */
  async fetchClinics() {
    const response = await this.httpClient.get(`${this.authBaseUrl}/api/user/clinics/fetch`);
    this.httpClient.checkResponse(response);
    return fromClinicDataList(await this.httpClient.parseJson(response));
  }

  /**
   * Fetch a specific user by ID
   * @param {string} userId - The ID of the user to fetch
   * @returns {Promise<import('./dto/UserData.js').UserData>}
   */
  async fetchUser(userId) {
    const response = await this.httpClient.get(`${this.authBaseUrl}/api/user/${userId}/fetch`);
    this.httpClient.checkResponse(response);
    return fromUserData(await this.httpClient.parseJson(response));
  }

  /**
   * Fetch all users for the signed-in user's clinic
   * @returns {Promise<import('./dto/UserData.js').UserData[]>}
   */
  async fetchClinicUsers(options = {}) {
    const response = await this.httpClient.get(`${this.authBaseUrl}/api/clinic/users/fetch`, options);
    this.httpClient.checkResponse(response);
    return fromUserDataList(await this.httpClient.parseJson(response));
  }

  /**
   * Fetch active users for the signed-in user's clinic
   * @returns {Promise<import('./dto/UserData.js').UserData[]>}
   */
  async fetchActiveClinicUsers() {
    const response = await this.httpClient.get(`${this.authBaseUrl}/api/clinic/users/active/fetch`);
    this.httpClient.checkResponse(response);
    return fromUserDataList(await this.httpClient.parseJson(response));
  }

  /**
   * Fetch all available roles and their abilities
   * @returns {Promise<Object<string, import('./dto/UserRoleData.js').UserRoleData>>}
   */
  async fetchRoles() {
    const response = await this.httpClient.get(`${this.authBaseUrl}/api/roles/fetch`);
    this.httpClient.checkResponse(response);
    return fromUserRoleMap(await this.httpClient.parseJson(response));
  }

  /**
   * Update a user's role
   * @param {string} userId - The ID of the user to update
   * @param {string} role - The new role identifier
   * @returns {Promise<Object>} Updated user data
   */
  async updateUserRole(userId, role) {
    const response = await this.httpClient.post(
      `${this.authBaseUrl}/api/user/${userId}/update`,
      toUserUpdateRequest({ role })
    );
    this.httpClient.checkResponse(response);
    return this.httpClient.parseJson(response);
  }

  /**
   * Update a user's details
   * @param {string} userId - The ID of the user to update
   * @param {Object} userData - The user data to update (e.g., {name: "New Name"})
   * @returns {Promise<Object>} Updated user data
   */
  async updateUserDetails(userId, userData) {
    const response = await this.httpClient.post(
      `${this.authBaseUrl}/api/user/${userId}/update`,
      toUserUpdateRequest({ name: userData.name })
    );
    this.httpClient.checkResponse(response);
    return this.httpClient.parseJson(response);
  }

  /**
   * Update the signed-in user's own name and/or availability.
   * @param {{ name?: string, markOutOfOffice?: boolean, returnsAt?: string|null }} fields
   * @returns {Promise<Object|null>}
   */
  async updateCurrentUser(fields) {
    const response = await this.httpClient.post(
      `${this.authBaseUrl}/api/user/update`,
      toUserUpdateRequest(fields)
    );
    this.httpClient.checkResponse(response);
    return this.httpClient.parseJson(response);
  }

  /**
   * Mark a user out of office, or back in office.
   * @param {string} userId
   * @param {{ markOutOfOffice: boolean, returnsAt?: string|null }} availability
   * @returns {Promise<Object|null>}
   */
  async updateUserAvailability(userId, { markOutOfOffice, returnsAt }) {
    const response = await this.httpClient.post(
      `${this.authBaseUrl}/api/user/${userId}/update`,
      toUserUpdateRequest({ markOutOfOffice, returnsAt })
    );
    this.httpClient.checkResponse(response);
    return this.httpClient.parseJson(response);
  }

  /**
   * Unblock a user who has been blocked due to failed verification
   * @param {string} userId - The ID of the user to unblock
   * @returns {Promise<Object>} Response data
   */
  async unblockUser(userId) {
    const response = await this.httpClient.post(
      `${this.authBaseUrl}/api/user/${userId}/unblock`
    );
    this.httpClient.checkResponse(response);
    return this.httpClient.parseJson(response);
  }

  /**
   * Reset a user's password
   * @param {string} userId - The ID of the user to reset
   * @returns {Promise<Object>} Response data
   */
  async resetUser(userId) {
    const response = await this.httpClient.post(
      `${this.authBaseUrl}/api/user/${userId}/reset`
    );
    this.httpClient.checkResponse(response);
    return this.httpClient.parseJson(response);
  }

  /**
   * Delete a user
   * @param {string} userId - The ID of the user to delete
   * @returns {Promise<Object>} Response data
   */
  async deleteUser(userId) {
    const response = await this.httpClient.post(
      `${this.authBaseUrl}/api/user/${userId}/delete`
    );
    this.httpClient.checkResponse(response);
    return this.httpClient.parseJson(response);
  }

  /**
   * Add a user to additional clinics
   * @param {string} userId - The ID of the user
   * @param {Array<string>} clinicIds - Clinic IDs to add the user to
   * @returns {Promise<Object>} Response data
   */
  async addUserToClinics(userId, clinicIds) {
    const response = await this.httpClient.post(
      `${this.authBaseUrl}/api/user/${userId}/update`,
      toUserUpdateRequest({ addedClinicIds: clinicIds })
    );
    this.httpClient.checkResponse(response);
    return this.httpClient.parseJson(response);
  }

  /**
   * Remove a user from clinics
   * @param {string} userId - The ID of the user
   * @param {Array<string>} clinicIds - Clinic IDs to remove the user from
   * @returns {Promise<Object>} Response data
   */
  async removeUserFromClinics(userId, clinicIds) {
    const response = await this.httpClient.post(
      `${this.authBaseUrl}/api/user/${userId}/update`,
      toUserUpdateRequest({ removedClinicIds: clinicIds })
    );
    this.httpClient.checkResponse(response);
    return this.httpClient.parseJson(response);
  }

  /**
   * Fetch issues reported by the current user
   * @returns {Promise<Array>} Issues data
   */
  async fetchUserIssues() {
    const response = await this.httpClient.get(`${this.authBaseUrl}/api/user/issues/fetch`);
    this.httpClient.checkResponse(response);
    return this.httpClient.parseJson(response);
  }

  /**
   * Fetch user settings (key-value pairs)
   * @returns {Promise<Object>} Settings object
   */
  async fetchUserSettings() {
    const response = await this.httpClient.get(`${this.authBaseUrl}/api/user/settings/fetch`);
    this.httpClient.checkResponse(response);
    return this.httpClient.parseJson(response);
  }

  /**
   * Update user settings
   * @param {Object} settings - Key-value pairs to update
   * @returns {Promise<Object>} Response data
   */
  async updateUserSettings(settings) {
    const response = await this.httpClient.post(
      `${this.authBaseUrl}/api/user/settings/update`,
      settings
    );
    this.httpClient.checkResponse(response);
    return this.httpClient.parseJson(response);
  }
}

